import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { getAutoStoryboardPreflight, autoGenerateScene, autoGenerateFull } from "@/services/shotService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, AlertTriangle, User, MapPin, Sparkles, Loader2, Image, X, RotateCcw } from "lucide-react";

export default function AutoStoryboardPreflight({
  open, onOpenChange, projectId, sceneNumber = null, onGenerationStarted
}) {
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [clearExisting, setClearExisting] = useState(false);
  const [style, setStyle] = useState("previs");

  const isFullMovie = sceneNumber === null;

  useEffect(() => {
    if (open && projectId) {
      fetchPreflight();
    }
  }, [open, projectId, sceneNumber]);

  const fetchPreflight = async () => {
    setLoading(true);
    try {
      const scenes = sceneNumber ? [sceneNumber] : null;
      const data = await getAutoStoryboardPreflight(projectId, scenes);
      setPreflight(data);
    } catch (err) {
      toast.error("Failed to load pre-flight check");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setStarting(true);
    try {
      if (isFullMovie) {
        await autoGenerateFull(projectId, 1, null, clearExisting);
      } else {
        await autoGenerateScene(projectId, sceneNumber, clearExisting);
      }
      toast.success(isFullMovie ? "Full storyboard generation started!" : `Scene ${sceneNumber} generation started!`);
      onOpenChange(false);
      if (onGenerationStarted) onGenerationStarted();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Generation already in progress for this project");
      } else {
        toast.error("Failed to start generation: " + (err.response?.data?.detail || err.message));
      }
    } finally {
      setStarting(false);
    }
  };

  const allReady = preflight && preflight.missing_character_count === 0 && preflight.missing_location_count === 0;

  const styles = [
    { value: "sketch", label: "Sketch", desc: "Charcoal pencil storyboard" },
    { value: "concept", label: "Concept", desc: "Digital painting" },
    { value: "previs", label: "Previs", desc: "Photorealistic" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-surface)] border-[var(--border-default)] max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg text-[var(--text-primary)] flex items-center gap-2">
            <Sparkles size={18} className="text-[var(--color-gold-400)]" />
            {isFullMovie ? "Auto Generate Full Storyboard" : `Auto Generate — Scene ${sceneNumber}`}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-[var(--color-gold-400)]" size={24} />
            <span className="ml-2 text-sm text-[var(--text-muted)]">Checking readiness...</span>
          </div>
        ) : preflight ? (
          <div className="space-y-5 mt-3">
            {/* Characters */}
            <div>
              <h3 className="text-sm font-medium text-[var(--text-primary)] mb-2 flex items-center gap-1.5">
                <User size={14} /> Characters
              </h3>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {preflight.characters?.map((char) => (
                  <div key={char.id || char.name} className="flex items-start gap-2 p-2 rounded-lg bg-[var(--bg-muted)]">
                    {char.has_description && char.has_reference_image ? (
                      <CheckCircle2 size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertTriangle size={14} className="text-[var(--color-gold-400)] mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-[var(--text-primary)]">{char.name}</span>
                      {char.description_preview && (
                        <p className="text-[10px] text-[var(--text-muted)] truncate">{char.description_preview}</p>
                      )}
                      <div className="flex gap-2 mt-0.5">
                        {!char.has_description && (
                          <span className="text-[10px] text-[var(--color-gold-400)]">No description</span>
                        )}
                        {!char.has_reference_image && (
                          <span className="text-[10px] text-[var(--color-gold-400)] flex items-center gap-0.5">
                            <Image size={10} /> No reference
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {(!preflight.characters || preflight.characters.length === 0) && (
                  <p className="text-xs text-[var(--text-muted)] p-2">No characters detected</p>
                )}
              </div>
            </div>

            {/* Locations */}
            <div>
              <h3 className="text-sm font-medium text-[var(--text-primary)] mb-2 flex items-center gap-1.5">
                <MapPin size={14} /> Locations
              </h3>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {preflight.locations?.map((loc) => (
                  <div key={loc.id || loc.name} className="flex items-center gap-2 p-2 rounded-lg bg-[var(--bg-muted)]">
                    {loc.has_reference_image || loc.has_3d_renders ? (
                      <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" />
                    ) : (
                      <AlertTriangle size={14} className="text-[var(--color-gold-400)] flex-shrink-0" />
                    )}
                    <span className="text-xs text-[var(--text-primary)]">{loc.name}</span>
                    {loc.has_3d_renders && <span className="text-[10px] text-green-500 ml-auto">3D</span>}
                  </div>
                ))}
                {(!preflight.locations || preflight.locations.length === 0) && (
                  <p className="text-xs text-[var(--text-muted)] p-2">No locations matched</p>
                )}
              </div>
            </div>

            {/* Style selector */}
            <div>
              <h3 className="text-sm font-medium text-[var(--text-primary)] mb-2">Style</h3>
              <div className="grid grid-cols-3 gap-2">
                {styles.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setStyle(s.value)}
                    className={`p-2.5 rounded-lg border text-center transition-all ${
                      style === s.value
                        ? 'border-[var(--color-gold-500)] bg-[rgba(212,168,83,0.1)]'
                        : 'border-[var(--border-subtle)] bg-[var(--bg-muted)] hover:border-[var(--border-default)]'
                    }`}
                  >
                    <p className="text-xs font-medium text-[var(--text-primary)]">{s.label}</p>
                    <p className="text-[10px] text-[var(--text-muted)]">{s.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Resume banner */}
            {isFullMovie && preflight.resume_info?.has_partial_generation && !clearExisting && (
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <div className="flex gap-2">
                  <RotateCcw size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-blue-300 leading-relaxed">
                    <p className="font-medium mb-0.5">Previous generation found</p>
                    <p>
                      {preflight.resume_info.scenes_completed} scene{preflight.resume_info.scenes_completed !== 1 ? 's' : ''} already generated ({preflight.resume_info.total_existing_frames} frames).
                      Will resume from Scene {preflight.resume_info.first_incomplete_scene}.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Options */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={clearExisting}
                onChange={(e) => setClearExisting(e.target.checked)}
                className="rounded border-[var(--border-default)]"
              />
              <span className="text-xs text-[var(--text-secondary)]">Clear existing auto-generated shots</span>
            </label>

            {/* API key warning */}
            {preflight.api_keys_configured && (!preflight.api_keys_configured.anthropic || !preflight.api_keys_configured.gemini) && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <div className="flex gap-2">
                  <AlertTriangle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-red-400 leading-relaxed">
                    <p className="font-medium mb-1">Missing API Keys</p>
                    {!preflight.api_keys_configured.anthropic && <p>ANTHROPIC_API_KEY not configured — script analysis will fail.</p>}
                    {!preflight.api_keys_configured.gemini && <p>GEMINI_API_KEY not configured — image generation will fail.</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Info banner */}
            {preflight.missing_character_count > 0 && (
              <div className="p-3 rounded-lg bg-[rgba(212,168,83,0.1)] border border-[rgba(212,168,83,0.2)]">
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                  {preflight.missing_character_count} character{preflight.missing_character_count > 1 ? 's' : ''} missing descriptions.
                  AI will infer appearances, but results are better with explicit descriptions and reference images.
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2">
              {(() => {
                const canResume = isFullMovie && preflight.resume_info?.has_partial_generation && !clearExisting;
                const icon = starting
                  ? <Loader2 size={14} className="animate-spin mr-1.5" />
                  : canResume
                    ? <RotateCcw size={14} className="mr-1.5" />
                    : <Sparkles size={14} className="mr-1.5" />;
                const label = canResume
                  ? `Resume from Scene ${preflight.resume_info.first_incomplete_scene}`
                  : !allReady
                    ? "Generate Anyway — AI Will Infer"
                    : "Start Generating";
                return (
                  <Button onClick={handleGenerate} className="btn-primary flex-1" disabled={starting}>
                    {icon}
                    {label}
                  </Button>
                );
              })()}
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-[var(--text-muted)]">
            Failed to load pre-flight data. Please try again.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
